import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, SearchIcon, FileTextIcon, DollarSignIcon, CheckCircleIcon, AlertTriangleIcon, MoreHorizontalIcon, EyeIcon, EditIcon, ArchiveIcon, MailIcon, DownloadIcon, TrashIcon } from 'lucide-react';
import { Invoice } from '../../../shared/types/Invoice';
export function Invoices() {
  const {
    t
  } = useTranslation('invoices');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Mock data
  const invoices: Invoice[] = [{
    id: '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+60 12 345 6789',
    amount: 125000,
    currency: getTenant().currency.code,
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    items: [],
    subtotal: 125000,
    total: 125000
  }, {
    id: '2',
    invoiceNumber: 'INV-2024-002',
    customerName: 'Tech Solutions Ltd',
    customerEmail: 'accounts@techsolutions.mv',
    customerPhone: '+60 11 234 5678',
    amount: 87500,
    currency: getTenant().currency.code,
    status: 'sent',
    dueDate: '2024-02-01',
    issueDate: '2024-01-15',
    description: 'Consulting services',
    items: [],
    subtotal: 87500,
    total: 87500
  }, {
    id: '3',
    invoiceNumber: 'INV-2024-003',
    customerName: 'Global Enterprises',
    customerEmail: 'finance@global.com',
    amount: 250000,
    currency: getTenant().currency.code,
    status: 'overdue',
    dueDate: '2024-01-20',
    issueDate: '2024-01-05',
    description: 'Product delivery',
    items: [],
    subtotal: 250000,
    total: 250000
  }, {
    id: '4',
    invoiceNumber: 'INV-2024-004',
    customerName: 'Innovation Hub',
    customerEmail: 'billing@innovationhub.mv',
    amount: 45000,
    currency: getTenant().currency.code,
    status: 'draft',
    dueDate: '2024-02-15',
    issueDate: '2024-01-25',
    description: 'Software license',
    items: [],
    subtotal: 45000,
    total: 45000
  }];
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${styles[status]}`} data-id="element-6640">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = searchQuery === '' || invoice.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || invoice.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = activeTab === 'all' || invoice.status === activeTab;
    return matchesSearch && matchesStatus;
  });
  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidAmount = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
  const overdueAmount = filteredInvoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-6641">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-6642">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6643">
          {t('pages.Invoices.invoices')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-6644">
          <button onClick={() => navigate('/invoice-form')} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6645">
            {t('pages.Invoices.add_invoice')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-6646">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-6647">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-6648">
            <FileTextIcon size={16} className="text-gray-500" data-id="element-6649" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-6650">
              {t('pages.Invoices.total_invoices')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-6651">
            <div className="text-2xl font-bold text-gray-900" data-id="element-6652">
              {filteredInvoices.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-6653">
            {t('pages.Invoices.all_time')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-6654">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-6655">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-6656" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-6657">
              {t('pages.Invoices.total_amount')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-6658">
            <div className="text-2xl font-bold text-gray-900" data-id="element-6659">
              {t('pages.InvoiceDetail.usd')}{' '}
              {totalAmount.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-6660">
            {t('pages.Invoices.across')} {filteredInvoices.length}{' '}
            {t('pages.Invoices.invoices_2')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-6661">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-6662">
            <CheckCircleIcon size={16} className="text-green-500" data-id="element-6663" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-6664">
              {t('pages.Invoices.paid')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-6665">
            <div className="text-2xl font-bold text-green-600" data-id="element-6666">
              {t('pages.InvoiceDetail.usd')}{' '}
              {paidAmount.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-green-50 text-xs text-green-700 font-medium" data-id="element-6667">
            {filteredInvoices.filter(i => i.status === 'paid').length}{' '}
            {t('pages.Invoices.invoices_2')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-6668">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-6669">
            <AlertTriangleIcon size={16} className="text-red-500" data-id="element-6670" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-6671">
              {t('pages.Invoices.overdue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-6672">
            <div className="text-2xl font-bold text-red-600" data-id="element-6673">
              {t('pages.InvoiceDetail.usd')}{' '}
              {overdueAmount.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-red-50 text-xs text-red-700 font-medium" data-id="element-6674">
            {filteredInvoices.filter(i => i.status === 'overdue').length}{' '}
            {t('pages.Invoices.invoices_2')}
          </div>
        </div>
      </div>

      <div className="space-y-6" data-id="element-6675">
        {/* Tabs + Controls */}
        <div className="flex items-end justify-between border-b border-gray-200" data-id="element-6676">
          <div className="flex space-x-6" role="tablist" aria-label="Invoices" data-id="element-6677">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'draft',
            label: 'Draft'
          }, {
            id: 'sent',
            label: 'Sent'
          }, {
            id: 'paid',
            label: 'Paid'
          }, {
            id: 'overdue',
            label: 'Overdue'
          }, {
            id: 'cancelled',
            label: 'Cancelled'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`invoices-panel-${tab.id}`} id={`invoices-tab-${tab.id}`} onClick={() => setActiveTab(tab.id)} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-6678">
                {tab.label}
              </button>)}
          </div>
          <div className="flex items-center gap-4 pb-3" data-id="element-6679">
            <div className="relative" data-id="element-6680">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-6681" />

              <input type="search" aria-label={t('pages.Invoices.attrs.search_invoices')} placeholder={t('pages.Invoices.attrs.search_invoices')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-6682" />
            </div>
            <label className="flex items-center cursor-pointer" data-id="element-6683">
              <div className="relative" data-id="element-6684">
                <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-6685" />

                <div className={`block w-9 h-5 rounded-full transition-colors ${showArchived ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-6686" />

                <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-6687" />
              </div>
              <span className="text-xs text-gray-600 ml-2 font-medium" data-id="element-6688">
                {t('pages.Invoices.show_archived')}
              </span>
            </label>
          </div>
        </div>

        {/* Invoices List */}
        {filteredInvoices.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-6689">
            <p className="text-gray-500 text-sm" data-id="element-6690">
              {t('pages.Invoices.no_invoices_found_matching_your_criteria')}
            </p>
          </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6691">
            <table className="w-full text-left border-collapse" data-id="element-6692">
              <tbody className="divide-y divide-gray-100" data-id="element-6693">
                {filteredInvoices.map(invoice => <tr key={invoice.id} onClick={() => navigate(`/invoice-detail/${invoice.id}`)} className="hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-6694">
                    <td className="px-5 py-4" data-id="element-6695">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-6696">
                        {invoice.customerName}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5" data-id="element-6697">
                        {invoice.invoiceNumber}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-6698">
                      <div className="text-sm font-medium text-gray-900" data-id="element-6699">
                        {t('pages.InvoiceDetail.usd')}{' '}
                        {invoice.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </div>
                    </td>
                    <td className="px-4 py-4" data-id="element-6700">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-6701">
                      <div className="text-sm text-gray-500" data-id="element-6702">
                        {t('pages.Invoices.due_on')}{' '}
                        {new Date(invoice.dueDate).toLocaleDateString('en-GB')}
                      </div>
                    </td>
                    <td className="px-4 py-4 w-12 text-right relative" data-id="element-6703">
                      <div ref={contextMenuOpenId === invoice.id ? contextMenuRef : null} data-id="element-6704">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === invoice.id ? null : invoice.id);
                  }} aria-label="Invoice actions" data-id="element-6705">
                          <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-6706" />
                        </button>

                        {contextMenuOpenId === invoice.id && <>
                            <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-6707" />

                            <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-6708">
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/invoice-detail/${invoice.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6709">
                                <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-6710" />

                                {t('pages.Invoices.details')}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/invoice-form?id=${invoice.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6711">
                                <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-6712" />

                                {t('pages.InvoiceDetail.edit')}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6713">
                                <MailIcon size={16} className="mr-3 text-gray-400" data-id="element-6714" />

                                {t('pages.Invoices.send_email')}
                              </button>
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-6715">
                                <DownloadIcon size={16} className="mr-3 text-gray-400" data-id="element-6716" />

                                {t('pages.Invoices.download_pdf')}
                              </button>
                              <div className="border-t border-gray-100 my-1" data-id="element-6717" />
                              <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-6718">
                                <ArchiveIcon size={16} className="mr-3" data-id="element-6719" />
                                {t('pages.InvoiceDetail.archive')}
                              </button>
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}